import { useState, useEffect } from "react";
import api from "../utils/api";
import { Megaphone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import WhatsNewCard from "./WhatsNewCard";

/**
 * Global "What's New" button + badge. Shows the count of unseen announcements
 * and opens a panel to browse the full history. Opening an item marks just
 * that one as seen (badge reflects genuinely-unread).
 */
export default function WhatsNewButton({ place = "" }) {
  const [feed, setFeed] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/whats-new/feed").then((r) => {
      setFeed(r.data?.announcements || []);
      setUnseen(r.data?.unseen_count || 0);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const markSeen = (a) => {
    if (a.seen) return;
    api.post(`/ui-flags/whatsnew_${a.id}`).catch(() => {});
    setFeed((f) => f.map((x) => (x.id === a.id ? { ...x, seen: true } : x)));
    setUnseen((n) => Math.max(0, n - 1));
  };

  const openItem = (a) => { markSeen(a); setSelected(a); };

  const markAll = () => {
    api.post("/whats-new/mark-all-seen").catch(() => {});
    setFeed((f) => f.map((x) => ({ ...x, seen: true })));
    setUnseen(0);
  };

  const handleCta = (a) => {
    const url = a?.button_url;
    if (!url) return;
    if (url.startsWith("http")) window.open(url, "_blank", "noopener");
    else window.location.href = url;
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setSelected(null); load(); }}
        data-testid={`whats-new-button${place ? `-${place}` : ""}`}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
        title="What's New"
        aria-label="What's New"
      >
        <Megaphone className="w-5 h-5" />
        {unseen > 0 && (
          <span
            data-testid="whats-new-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unseen > 9 ? "9+" : unseen}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 overflow-hidden sm:max-w-[440px]" data-testid="whats-new-center">
          <DialogTitle className="sr-only">What's New</DialogTitle>
          {selected ? (
            <WhatsNewCard
              announcement={selected}
              dismissLabel="← Back"
              onCta={() => handleCta(selected)}
              onDismiss={() => setSelected(null)}
            />
          ) : (
            <div data-testid="whats-new-list">
              <div className="flex items-center gap-2 px-5 py-4 border-b">
                <Megaphone className="w-4 h-4 text-indigo-600" />
                <h2 className="font-bold text-gray-900">What's New</h2>
                {unseen > 0 && (
                  <button onClick={markAll} data-testid="whats-new-mark-all" className="ml-auto text-xs text-indigo-600 hover:underline">
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {feed.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-10">No updates yet.</p>
                ) : feed.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => openItem(a)}
                    data-testid={`whats-new-feed-item-${a.id}`}
                    className="w-full text-left flex items-start gap-3 px-5 py-3 border-b hover:bg-gray-50 transition-colors"
                  >
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${a.seen ? "" : "bg-indigo-500"}`} />
                    {a.image_url && <img src={a.image_url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm truncate ${a.seen ? "text-gray-600" : "font-semibold text-gray-900"}`}>{a.title}</p>
                      <p className="text-xs text-gray-400">{fmt(a.created_at)}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
